import { useEffect, type ReactNode } from "react";

/**
 * Panelin tek diyalog bileşeni.
 *
 * `prompt()` / `confirm()` yerine geçiyor: tarayıcının yerleşik diyalogları
 * tek satır metin alıyor, biçimlendirilemiyor ve sayfayı dondurduğu için
 * arkada bir işlem sürerken kullanılamıyor. Çok satırlı cevap girişi ve
 * içe aktarma önizlemesi ikisini de gerektiriyor.
 */
export default function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Arkadaki liste kaymasın — uzun listelerde modal açıkken tekerlek
    // gövdeyi kaydırıyordu.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      <div
        className="fixed inset-0 bg-bgDeep/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={[
          "relative card w-full my-auto",
          wide ? "max-w-4xl" : "max-w-xl",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-4 px-6 pt-5 pb-4 border-b border-stroke">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold leading-tight">{title}</h2>
            {subtitle && (
              <p className="text-xs text-textMuted mt-1">{subtitle}</p>
            )}
          </div>
          <button
            className="text-textMuted hover:text-textPrimary text-xl leading-none px-2 -mr-2"
            onClick={onClose}
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5">{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-stroke flex items-center justify-end gap-2 flex-wrap">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
